import { useState, useRef, useCallback, useEffect } from 'react';

// Use the validated API key
const ELEVENLABS_API_KEY = 'sk_6f556bde3bbc03e4115bd8e409d51fc8b7c122683f98a630';

interface Advisor {
  id: string;
  name: string;
  title: string;
  description?: string;
  personality_prompt?: string;
  elevenlabs_agent_id?: string;
  gender?: string;
  is_default?: boolean;
  voice_settings?: any;
}

interface VoiceMessage {
  speaker: string;
  text: string;
  timestamp: number;
  isUser: boolean;
  advisorId?: string;
}

export const useVoiceBoardroom = () => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<VoiceMessage[]>([]);
  const [currentSpeaker, setCurrentSpeaker] = useState<string | null>(null);
  
  // Single conversation ref and simple state tracking
  const conversationRef = useRef<any>(null);
  const isActiveRef = useRef(false);
  const sessionIdRef = useRef<string | null>(null);

  // Simplified cleanup function
  const cleanup = useCallback(async () => {
    console.log('🧹 Cleaning up voice session...');
    
    const conversation = conversationRef.current;
    
    // Clear state immediately
    conversationRef.current = null;
    isActiveRef.current = false;
    sessionIdRef.current = null;
    setIsConnecting(false);
    setIsConnected(false);
    setCurrentSpeaker(null);

    // Clean up conversation
    if (conversation) {
      try {
        await conversation.endSession();
        console.log('✅ Voice session ended successfully');
      } catch (e) {
        console.warn('⚠️ Error ending voice session:', e);
      }
    }
  }, []);

  // Get agent ID from advisor data
  const getAgentIdForAdvisor = useCallback((advisor: Advisor): string => {
    // Use the database agent ID if available
    if (advisor.elevenlabs_agent_id && advisor.elevenlabs_agent_id.startsWith('agent_')) {
      return advisor.elevenlabs_agent_id;
    }
    
    // Fallback mapping for default advisors
    const agentMapping: Record<string, string> = {
      'Beatrice': 'agent_01jz125yzgeyctcjc029ka5g76',
      'Samuel': 'agent_01jz112ggbfpps9df439se858p',
      'Leo': 'agent_01jz11nhmgf1hv7v63tt7kgjth',
      'Marcus': 'agent_01jz12119wfvdrn9kexm7mv0mr'
    };
    
    return agentMapping[advisor.name] || '';
  }, []);

  // Simplified start session - let ElevenLabs agents use their configured personalities
  const startSession = useCallback(async (
    advisorOrAdvisors: Advisor | Advisor[], 
    userName: string,
    conversationId?: string
  ) => {
    // Generate unique session ID
    const sessionId = `voice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    console.log(`🎤 Starting voice session ${sessionId}...`);
    
    // Prevent multiple sessions
    if (isActiveRef.current || conversationRef.current) {
      console.log(`⚠️ Voice session already active, cleaning up first...`);
      await cleanup();
      // Small delay to ensure cleanup is complete
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    const advisors = Array.isArray(advisorOrAdvisors) ? advisorOrAdvisors : [advisorOrAdvisors];
    const primaryAdvisor = advisors[0];

    console.log(`🚀 Starting voice session with ${primaryAdvisor.name} (${advisors.length} advisor${advisors.length > 1 ? 's' : ''})`);
    
    sessionIdRef.current = sessionId;
    setError(null);
    setIsConnecting(true);
    setCurrentSpeaker(null);

    try {
      // Validate API key
      if (!ELEVENLABS_API_KEY || !ELEVENLABS_API_KEY.startsWith('sk_')) {
        throw new Error('ElevenLabs API key is not set or invalid');
      }

      // Get agent ID for primary advisor
      const agentId = getAgentIdForAdvisor(primaryAdvisor);
      if (!agentId) {
        throw new Error(`No ElevenLabs agent ID found for ${primaryAdvisor.name}`);
      }

      console.log(`🤖 Using agent ID: ${agentId} for ${primaryAdvisor.name}`);

      // Import ElevenLabs SDK
      const { Conversation } = await import('@elevenlabs/client');
      
      console.log(`💬 Starting ElevenLabs conversation (no system prompt override)`);

      // Start the ElevenLabs conversation - let the agent use its configured personality
      const conversation = await Conversation.startSession({
        agentId: agentId,
        requiresAuth: false,
        // No system prompt override - let the agent use its configured personality
        
        onConnect: () => {
          console.log(`🎯 Voice session ${sessionId} connected successfully`);
          isActiveRef.current = true;
          setIsConnected(true);
          setIsConnecting(false);
          setError(null);
        },
        
        onDisconnect: () => {
          console.log(`🔌 Voice session ${sessionId} disconnected`);
          
          if (sessionIdRef.current === sessionId) {
            isActiveRef.current = false;
            conversationRef.current = null;
            sessionIdRef.current = null;
            setIsConnected(false);
            setCurrentSpeaker(null);
          }
        },
        
        onError: (error: any) => {
          console.error(`❌ ElevenLabs voice error for session ${sessionId}:`, error);
          
          let errorMessage = 'Voice connection failed';
          if (error.message?.includes('401') || error.message?.includes('unauthorized')) {
            errorMessage = 'Invalid ElevenLabs API key';
          } else if (error.message?.includes('404')) {
            errorMessage = `Agent ${agentId} not found in your ElevenLabs account`;
          } else if (error.message?.includes('403') || error.message?.includes('forbidden')) {
            errorMessage = 'Conversational AI not enabled. Please upgrade your ElevenLabs plan';
          } else if (error.message?.includes('websocket') || error.message?.includes('connection')) {
            errorMessage = 'Voice connection failed. Please check your internet connection';
          } else {
            errorMessage = error.message || error.toString() || 'Unknown voice error';
          }
          
          if (sessionIdRef.current === sessionId) {
            setError(errorMessage);
            setIsConnecting(false);
            setIsConnected(false);
            setCurrentSpeaker(null);
            isActiveRef.current = false;
            conversationRef.current = null;
            sessionIdRef.current = null;
          }
        },
        
        onMessage: (message: any) => {
          // Only process messages for the current session
          if (sessionIdRef.current !== sessionId || !isActiveRef.current) {
            return;
          }

          console.log(`📨 Voice message received for session ${sessionId}:`, message);
          
          try {
            const isUserMessage = message.source === 'user' || message.type === 'user_transcript';
            let text = message.text || message.message || message.content || message.transcript || '';
            let speakerName = isUserMessage ? userName : primaryAdvisor.name;
            
            // Update current speaker
            if (!isUserMessage) {
              setCurrentSpeaker(primaryAdvisor.name);
            } else {
              setCurrentSpeaker(null);
            }
            
            // Add to transcript
            if (text && text.trim()) {
              const voiceMessage: VoiceMessage = {
                speaker: speakerName,
                text: text.trim(),
                timestamp: Date.now(),
                isUser: isUserMessage,
                advisorId: isUserMessage ? undefined : primaryAdvisor.id
              };
              
              setTranscript(prev => [...prev, voiceMessage]);
              console.log(`📝 Added to transcript: ${speakerName}: ${text.trim().substring(0, 50)}...`);
            }
          } catch (messageError) {
            console.warn(`⚠️ Error processing voice message:`, messageError);
          }
        }
      });

      // Store conversation reference
      conversationRef.current = conversation;
      console.log(`✅ Voice conversation ${sessionId} started successfully`);

    } catch (error) {
      console.error(`💥 Failed to start voice session ${sessionId}:`, error);
      
      if (sessionIdRef.current === sessionId) {
        setError(error instanceof Error ? error.message : 'Voice connection failed');
        setIsConnecting(false);
        setIsConnected(false);
        setCurrentSpeaker(null);
        isActiveRef.current = false;
        conversationRef.current = null;
        sessionIdRef.current = null;
      }
    }
  }, [cleanup, getAgentIdForAdvisor]);

  const endSession = useCallback(async () => {
    console.log('🛑 Ending voice session manually...');
    await cleanup();
  }, [cleanup]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const setMicMuted = useCallback((muted: boolean) => {
    if (conversationRef.current && isActiveRef.current) {
      try {
        conversationRef.current.setMicMuted(muted);
        console.log(`🎤 Microphone ${muted ? 'muted' : 'unmuted'}`);
      } catch (e) {
        console.warn('⚠️ Error setting mute:', e);
      }
    }
  }, []);

  const getSessionInfo = useCallback(() => ({
    isActive: isActiveRef.current,
    hasConversation: !!conversationRef.current,
    sessionId: sessionIdRef.current
  }), []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    startSession,
    endSession,
    clearError,
    setMicMuted,
    isConnecting,
    isConnected,
    error,
    transcript,
    currentSpeaker,
    getSessionInfo,
  };
};